import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { CheckCircle, Briefcase, User, GraduationCap, Users, Car, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const JobApplicationPublic = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [resubmissionToken, setResubmissionToken] = useState(null);
  const [formData, setFormData] = useState({
    surname: '',
    first_name: '',
    date_of_birth: '',
    residence: '',
    telephone: '',
    email: '',
    education: '',
    present_job: '',
    employer_name: '',
    employer_location: '',
    referee1_name: '',
    referee1_phone: '',
    referee1_address: '',
    referee2_name: '',
    referee2_phone: '',
    referee2_address: '',
    driving_experience: '',
    has_driving_license: false,
    position_applied: ''
  });

  // Check for resubmission token in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setResubmissionToken(token);
    }
  }, []);

  const POSITIONS = [
    'Farm Associate',
    'Sales Associate',
    'Snail House Associate',
    'Driver',
    'Distributor'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields (email is now required)
    if (!formData.surname || !formData.first_name || !formData.date_of_birth || 
        !formData.residence || !formData.telephone || !formData.email ||
        !formData.education ||
        !formData.referee1_name || !formData.referee1_phone || !formData.referee1_address ||
        !formData.referee2_name || !formData.referee2_phone || !formData.referee2_address ||
        !formData.driving_experience || !formData.position_applied) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        surname: formData.surname,
        first_name: formData.first_name,
        date_of_birth: formData.date_of_birth,
        residence: formData.residence,
        telephone: formData.telephone,
        email: formData.email,
        education: formData.education,
        present_job: formData.present_job || null,
        employer_name: formData.employer_name || null,
        employer_location: formData.employer_location || null,
        referee1: {
          name: formData.referee1_name,
          phone: formData.referee1_phone,
          address: formData.referee1_address
        },
        referee2: {
          name: formData.referee2_name,
          phone: formData.referee2_phone,
          address: formData.referee2_address
        },
        driving_experience: formData.driving_experience,
        has_driving_license: formData.has_driving_license,
        position_applied: formData.position_applied,
        resubmission_token: resubmissionToken
      };

      const response = await axios.post(`${API}/forms/job-application`, submitData);
      setReferenceNumber(response.data.reference_number);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Application Submitted!
          </h1>
          <p className="text-slate-600 mb-4">
            Thank you for your interest in joining Wilyrash Farm. Your application has been received and will be reviewed by our team.
          </p>
          
          {/* Reference Number Display */}
          <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl p-4 mb-4">
            <p className="text-xs text-slate-500 mb-2">Your Reference Number:</p>
            <p className="text-2xl font-bold text-slate-800 tracking-wider">{referenceNumber}</p>
            <p className="text-xs text-slate-400 mt-2">Save this number to check your application status</p>
          </div>
          
          <p className="text-sm text-slate-500 mb-4">
            We will contact you via the phone number you provided if your application is shortlisted.
          </p>
          
          <div className="flex flex-col gap-3">
            <Button 
              onClick={() => window.location.href = '/apply/status'}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Check Application Status
            </Button>
            <Button 
              onClick={() => {
                setSubmitted(false);
                setReferenceNumber('');
                setFormData({
                  surname: '', first_name: '', date_of_birth: '', residence: '', telephone: '', email: '',
                  education: '', present_job: '', employer_name: '', employer_location: '',
                  referee1_name: '', referee1_phone: '', referee1_address: '',
                  referee2_name: '', referee2_phone: '', referee2_address: '',
                  driving_experience: '', has_driving_license: false, position_applied: ''
                });
              }}
              variant="outline"
            >
              Submit Another Application
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_egg-inventory-pro/artifacts/vtgmvoj5_Logo%201.jpg" 
            alt="Wilyrash Farm Logo" 
            className="w-20 h-20 rounded-full object-cover mx-auto mb-4 shadow-lg"
          />
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Wilyrash Farm
          </h1>
          <p className="text-lg text-emerald-700 font-medium">Job Application Form</p>
          <p className="text-slate-500 mt-2 text-sm">
            Complete all required fields (*) to submit your application
          </p>
        </div>

        {/* Resubmission Notice */}
        {resubmissionToken && (
          <div className="mb-6 bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800">Resubmission Mode</h3>
              <p className="text-sm text-amber-700">
                You are using a special link to submit a new application. This link can only be used once.
              </p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Personal Information */}
          <div className="p-6 border-b">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-800">Personal Information</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Surname (Last Name) *</Label>
                <Input 
                  value={formData.surname} 
                  onChange={(e) => setFormData({...formData, surname: e.target.value})} 
                  placeholder="Enter surname"
                  required 
                />
              </div>
              <div>
                <Label>First Name *</Label>
                <Input 
                  value={formData.first_name} 
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})} 
                  placeholder="Enter first name"
                  required 
                />
              </div>
              <div>
                <Label>Date of Birth *</Label>
                <Input 
                  type="date" 
                  value={formData.date_of_birth} 
                  onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} 
                  required 
                />
              </div>
              <div>
                <Label>Telephone Number *</Label>
                <Input 
                  value={formData.telephone} 
                  onChange={(e) => setFormData({...formData, telephone: e.target.value})} 
                  placeholder="e.g., 08012345678"
                  required 
                />
              </div>
              <div className="md:col-span-2">
                <Label>Residence (Address) *</Label>
                <Textarea 
                  value={formData.residence} 
                  onChange={(e) => setFormData({...formData, residence: e.target.value})} 
                  placeholder="Enter your full residential address"
                  required 
                />
              </div>
              <div className="md:col-span-2">
                <Label>Email Address *</Label>
                <Input 
                  type="email"
                  value={formData.email} 
                  onChange={(e) => setFormData({...formData, email: e.target.value})} 
                  placeholder="Enter your email address"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">Required for application updates and interview notifications</p>
              </div>
            </div>
          </div>

          {/* Education & Employment */}
          <div className="p-6 border-b bg-slate-50">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-800">Education & Employment</h2>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Education *</Label>
                <Textarea 
                  value={formData.education} 
                  onChange={(e) => setFormData({...formData, education: e.target.value})} 
                  placeholder="Enter your educational background (schools attended, qualifications, certificates, etc.)"
                  required 
                />
              </div>
              <div>
                <Label>Present Job (if employed)</Label>
                <Input 
                  value={formData.present_job} 
                  onChange={(e) => setFormData({...formData, present_job: e.target.value})} 
                  placeholder="Enter your current job title"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Employer Name</Label>
                  <Input 
                    value={formData.employer_name} 
                    onChange={(e) => setFormData({...formData, employer_name: e.target.value})} 
                    placeholder="Name of current employer"
                  />
                </div>
                <div>
                  <Label>Employer Location</Label>
                  <Input 
                    value={formData.employer_location} 
                    onChange={(e) => setFormData({...formData, employer_location: e.target.value})} 
                    placeholder="Location of employer"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Referees/Guarantors */}
          <div className="p-6 border-b">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-800">Referees / Guarantors</h2>
            </div>
            
            {/* Referee 1 */}
            <div className="bg-slate-50 p-4 rounded-lg mb-4">
              <p className="text-sm font-medium text-slate-700 mb-3">Referee/Guarantor 1 *</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Full Name *</Label>
                  <Input 
                    value={formData.referee1_name} 
                    onChange={(e) => setFormData({...formData, referee1_name: e.target.value})} 
                    placeholder="Referee's full name"
                    required 
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone Number *</Label>
                  <Input 
                    value={formData.referee1_phone} 
                    onChange={(e) => setFormData({...formData, referee1_phone: e.target.value})} 
                    placeholder="Referee's phone"
                    required 
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs">Address *</Label>
                  <Input 
                    value={formData.referee1_address} 
                    onChange={(e) => setFormData({...formData, referee1_address: e.target.value})} 
                    placeholder="Referee's address"
                    required 
                  />
                </div>
              </div>
            </div>

            {/* Referee 2 */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-slate-700 mb-3">Referee/Guarantor 2 *</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Full Name *</Label>
                  <Input 
                    value={formData.referee2_name} 
                    onChange={(e) => setFormData({...formData, referee2_name: e.target.value})} 
                    placeholder="Referee's full name"
                    required 
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone Number *</Label>
                  <Input 
                    value={formData.referee2_phone} 
                    onChange={(e) => setFormData({...formData, referee2_phone: e.target.value})} 
                    placeholder="Referee's phone"
                    required 
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs">Address *</Label>
                  <Input 
                    value={formData.referee2_address} 
                    onChange={(e) => setFormData({...formData, referee2_address: e.target.value})} 
                    placeholder="Referee's address"
                    required 
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Driving & Position */}
          <div className="p-6 bg-slate-50">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Car className="w-5 h-5 text-emerald-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-800">Driving & Position</h2>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Driving Experience *</Label>
                <Textarea 
                  value={formData.driving_experience} 
                  onChange={(e) => setFormData({...formData, driving_experience: e.target.value})} 
                  placeholder="Describe your driving experience (years of experience, types of vehicles driven, etc.). Write 'None' if you have no driving experience."
                  required 
                />
              </div>
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                <input 
                  type="checkbox" 
                  id="has_license"
                  checked={formData.has_driving_license}
                  onChange={(e) => setFormData({...formData, has_driving_license: e.target.checked})}
                  className="w-5 h-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                <Label htmlFor="has_license" className="cursor-pointer text-slate-700">
                  I have a valid driving license
                </Label>
              </div>
              <div>
                <Label>Position Applying For *</Label>
                <Select 
                  value={formData.position_applied} 
                  onValueChange={(v) => setFormData({...formData, position_applied: v})}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select the position you are applying for" />
                  </SelectTrigger>
                  <SelectContent>
                    {POSITIONS.map(pos => (
                      <SelectItem key={pos} value={pos}>
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-slate-400" />
                          {pos}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="p-6 bg-white border-t">
            <Button 
              type="submit" 
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-lg py-6"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </Button>
            <p className="text-xs text-slate-500 text-center mt-4">
              By submitting this application, you confirm that all information provided is accurate.
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-slate-500">
          <p>Wilyrash Farm - Poultry Farm Management</p>
        </div>
      </div>
    </div>
  );
};

export default JobApplicationPublic;
